import {
  AuthUser,
  CreatePasswordFormValues,
  ForgotPasswordFormValues,
  InformationPageForm,
  LoginFormValues,
  ResetPasswordFormValues
} from "../types"
import { Action, Dispatch } from "redux"
import { mapToCamelCase, mapToSnakeCase } from "../utility"
import { toast } from "react-toastify"
import { errorFilledToastOptions, infoFilledToastOptions, successFilledToastOptions } from "../constants"
import { codeValidation as codeValidationApi, authLogin } from "../apis"
import { lang } from "../constants/languages"

export const AUTH_ACTION = {
  LOGIN: "@AUTH/LOGIN",
  LOGIN_ERROR: "@AUTH/LOGIN_ERROR",
  LOGOUT: "@AUTH/LOGOUT",
  LOGOUT_ERROR: "@AUTH/LOGOUT_ERROR",
  UPDATE_USER: "@AUTH/UPDATE_USER",
  UPLOAD_PROFILE_PHOTO: "@AUTH/UPLOAD_PROFILE_PHOTO",
  FORGOT_PASSWORD: "@AUTH/FORGOT_PASSWORD",
  RESET_PASSWORD: "@AUTH/RESET_PASSWORD",
  CREATE_PASSWORD: "@AUTH/CREATE_PASSWORD",
  GET_ROLE: "@AUTH/GET_ROLE",
  SEND_VALIDATION: "@AUTH/SEND_VALIDATION",
  EMAIL_VALIDATE: "@AUTH/EMAIL_VALIDATE"
}
export const login = (formValues: LoginFormValues, callback: () => void) => async (dispatch: Dispatch<Action>) => {
  try {
    localStorage.removeItem("appliedAffiliate")
    const response = await authLogin.post("/", formValues)
    const data = mapToCamelCase<AuthUser>(response.data)

    dispatch({
      type: AUTH_ACTION.LOGIN,
      payload: data
    })
    callback()
  } catch (e) {
    console.log(e)
    toast.error(lang.login.error_on_login, errorFilledToastOptions)
    dispatch({
      type: AUTH_ACTION.LOGIN_ERROR,
      payload: e
    })
  }
}
export const logout = (callback: () => void) => async (dispatch: Dispatch<Action>) => {
  localStorage.removeItem("appliedAffiliate")
  dispatch({
    type: AUTH_ACTION.LOGOUT
  })
  callback()
}
export const forgotPassword = (formValues: ForgotPasswordFormValues, callback: Function) => async () => {
  try {
    toast.loading(lang.forgot_password.loading_text, {
      toastId: AUTH_ACTION.FORGOT_PASSWORD,
      ...infoFilledToastOptions
    })
    const response = await codeValidationApi.post("/send_validation/", mapToSnakeCase(formValues))
    toast.dismiss(AUTH_ACTION.FORGOT_PASSWORD)
    if (response.status === 200) {
      toast.success(lang.forgot_password.success_on_code_sent, successFilledToastOptions)
      callback()
    } else {
      toast.error(lang.forgot_password.error_on_code_sent, errorFilledToastOptions)
    }
  } catch {
    toast.error(lang.forgot_password.error_on_code_sent, errorFilledToastOptions)
  }
}
export const resetPassword = (formValues: ResetPasswordFormValues, callback?: Function) => async () => {
  try {
    toast.loading(lang.reset_password.loading_text, { toastId: AUTH_ACTION.RESET_PASSWORD, ...infoFilledToastOptions })
    const response = await codeValidationApi.put("/change_password/", formValues)
    toast.dismiss(AUTH_ACTION.RESET_PASSWORD)
    if (response.status === 200) {
      toast.success(lang.reset_password.success_on_code_reset, successFilledToastOptions)
      callback()
    } else {
      toast.error(lang.reset_password.error_on_code_reset, errorFilledToastOptions)
    }
  } catch {
    toast.error(lang.reset_password.error_on_code_reset, errorFilledToastOptions)
  }
}
export const createPassword = (formValues: CreatePasswordFormValues, callback?: Function) => async () => {
  try {
    toast.loading(lang.reset_password.loading_text, { toastId: AUTH_ACTION.CREATE_PASSWORD, ...infoFilledToastOptions })
    const response = await codeValidationApi.put("/create_password/", formValues)
    toast.dismiss(AUTH_ACTION.CREATE_PASSWORD)
    if (response.status === 200) {
      toast.success(lang.create_password.success_on_code_creating, successFilledToastOptions)
      if (callback) {
        callback()
      }
    } else {
      toast.error(lang.create_password.error_on_code_creating, errorFilledToastOptions)
    }
  } catch (error: any) {
    toast.dismiss(AUTH_ACTION.CREATE_PASSWORD)
    if (error?.response?.data?.message === "Password already created") {
      toast.error(lang.create_password.password_already_created_error, errorFilledToastOptions)
    } else {
      toast.error(lang.create_password.error_on_code_creating, errorFilledToastOptions)
    }
  }
}

export const emailValidation =
  (formValues: LoginFormValues, userCheck: boolean, useCredentials: boolean, subject: string, callback?: Function) =>
  async () => {
    try {
      toast.loading(lang.start_your_journey.information_page.validation_code_sending_loading_text, {
        toastId: AUTH_ACTION.SEND_VALIDATION,
        ...infoFilledToastOptions
      })

      const userCheckParam = userCheck ? "" : `&user_check=0`
      const useCredentialsParam = `&use_credentials=${useCredentials ? "1" : "0"}`
      const response = await codeValidationApi.post(
        `/send_validation/?subject=${subject}${userCheckParam}${useCredentialsParam}`,
        mapToSnakeCase(formValues)
      )
      toast.dismiss(AUTH_ACTION.SEND_VALIDATION)
      if (response.status === 200) {
        if (response.data.override_code_validation && callback) {
          callback(response.data.override_code_validation, false)
          return
        }
        if (response.data.message === "Enter 2FA code") {
          toast.success("Enter 2FA code", successFilledToastOptions)
        } else {
          toast.success(
            lang.start_your_journey.information_page.validation_code_sending_success,
            successFilledToastOptions
          )
        }

        if (callback) {
          callback(response.data.override_code_validation, response.data.enabled_2fa)
        }
      } else {
        toast.error(lang.start_your_journey.information_page.validation_code_sending_error, errorFilledToastOptions)
      }
    } catch {
      toast.error(lang.start_your_journey.information_page.validation_code_sending_error, errorFilledToastOptions)
    }
  }

export const validateCode = (formValues: LoginFormValues, callback?: Function) => async () => {
  try {
    toast.loading(lang.start_your_journey.information_page.email_validation_loading_text, {
      toastId: AUTH_ACTION.EMAIL_VALIDATE,
      ...infoFilledToastOptions
    })
    const response = await codeValidationApi.post("/validate_email_address/", {
      ...formValues
    })
    toast.dismiss(AUTH_ACTION.EMAIL_VALIDATE)
    if (response.status === 200) {
      toast.success(lang.start_your_journey.information_page.email_validation_success, successFilledToastOptions)
      if (callback) {
        callback()
      }
    } else {
      toast.error(lang.start_your_journey.information_page.email_validation_error, errorFilledToastOptions)
    }
  } catch {
    toast.error(lang.start_your_journey.information_page.email_validation_error, errorFilledToastOptions)
  }
}
